import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import type { Components } from 'react-markdown'

import { cn } from '@/shared/lib/utils'

/**
 * AttributeRichText - the single, safe renderer for rich text that comes from
 * dynamic-attribute configuration (labels, disclaimer copy, hints, etc.).
 *
 * WHY THIS EXISTS
 * ---------------
 * Attribute copy is authored as data (seeds / config packages / the Entity
 * Editor), never in the component tree. Some of that copy needs light rich
 * formatting - most commonly hyperlinks (a Terms & Conditions acknowledgement
 * that links out to the tenant's legal pages), plus the occasional bold/italic
 * emphasis or list. Every attribute renderer that shows such copy MUST route it
 * through this component instead of:
 *   - rendering the raw string as plain text (links never resolve), or
 *   - reaching for `dangerouslySetInnerHTML` (an XSS surface for seeded/config
 *     data).
 *
 * WHAT IT SUPPORTS
 * ----------------
 * A restricted **Markdown** subset via `react-markdown` + `remark-gfm`:
 *   - links: `[Terms & Conditions](https://...)` -> a safe `<a>` that opens in
 *     a new tab (`target="_blank" rel="noreferrer noopener"`),
 *   - emphasis: `**bold**`, `*italic*`,
 *   - lists and line breaks.
 * `react-markdown` does NOT render embedded raw HTML by default (we never add
 * `rehype-raw`), so authored copy cannot inject arbitrary markup - only the
 * whitelisted node types above render. This is the deliberate, safe ceiling for
 * attribute copy.
 *
 * VARIANTS
 * --------
 *   - `block` (default): renders as flowing paragraphs (used for disclaimer
 *     body copy and any multi-line notice).
 *   - `inline`: collapses the top-level paragraph into an inline `<span>` so the
 *     copy can sit next to a control (e.g. as a checkbox label) without forcing
 *     a block break.
 *
 * USAGE PATTERN (follow this for ALL attribute rich text)
 * -------------------------------------------------------
 * Any new attribute surface that needs formatted copy renders it here:
 *   <AttributeRichText content={field.configuration_data?.disclaimer_text} />
 * Authors put Markdown in the attribute's `configuration_data` (or
 * `display_name`/`description`); the value travels to the client verbatim and
 * this component renders it. Do not add a second markdown/HTML renderer for
 * attributes - extend this one.
 */

const LINK_CLASSNAME =
  'font-medium text-[var(--green)] underline underline-offset-2 hover:text-[var(--green-hover)]'

function buildComponents(inline: boolean): Components {
  return {
    a: ({ children, href }) => (
      <a
        href={href}
        target="_blank"
        rel="noreferrer noopener"
        className={LINK_CLASSNAME}
      >
        {children}
      </a>
    ),
    // In inline mode the wrapping paragraph must not introduce a block break or
    // margins so the copy flows as the label of the adjacent control.
    p: ({ children }) =>
      inline ? (
        <span>{children}</span>
      ) : (
        <p className="m-0 [&+p]:mt-2">{children}</p>
      ),
    ul: ({ children }) => <ul className="m-0 mt-1 list-disc pl-4">{children}</ul>,
    ol: ({ children }) => <ol className="m-0 mt-1 list-decimal pl-4">{children}</ol>,
    li: ({ children }) => <li className="mt-0.5">{children}</li>,
    strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
    em: ({ children }) => <em className="italic">{children}</em>,
  }
}

const BLOCK_COMPONENTS = buildComponents(false)
const INLINE_COMPONENTS = buildComponents(true)

export type AttributeRichTextProps = {
  /** Markdown source. Empty / non-string values render nothing. */
  content: unknown
  /** `inline` collapses the top-level paragraph into a span. */
  variant?: 'block' | 'inline'
  className?: string
}

export function AttributeRichText({
  content,
  variant = 'block',
  className,
}: AttributeRichTextProps) {
  if (typeof content !== 'string' || content.trim() === '') return null

  const inline = variant === 'inline'
  const Wrapper = inline ? 'span' : 'div'

  return (
    <Wrapper className={cn(inline && 'inline', className)}>
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={inline ? INLINE_COMPONENTS : BLOCK_COMPONENTS}
      >
        {content}
      </ReactMarkdown>
    </Wrapper>
  )
}
